// src/ClinicalDataCollector/components/output/AnnualWellnessSummary.jsx
import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Box,
  Paper,
  Alert,
  Button,
  Collapse,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HomeIcon from '@mui/icons-material/Home';
import ElderlyIcon from '@mui/icons-material/Elderly';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useSelector } from 'react-redux';

import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

const Section = ({ icon, title, children }) => (
  <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
    <Typography
      variant="h6"
      sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
    >
      {icon}
      <Box ml={1}>{title}</Box>
    </Typography>
    {children}
  </Paper>
);

const AnnualWellnessSummary = () => {
  // 🔹 id de AdvancedMD desde Redux
  const { advancedMdPatientId } = useSelector(
    state => state.patientMapSelect
  );

  // 🔹 notas Annual Wellness desde el contexto
  const {
    annualWellnessNotes,
    annualWellnessLoading,
    annualWellnessError,
    loadAnnualWellnessNotes,
  } = useContext(AdvancedMdContext);

  // 🔹 mapa de notas abiertas/cerradas
  const [openMap, setOpenMap] = useState({});

  const toggleNote = noteId => {
    setOpenMap(prev => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  // 🔹 cargar notas cuando tengamos patientId
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadAnnualWellnessNotes(advancedMdPatientId);
  }, [advancedMdPatientId, loadAnnualWellnessNotes]);

  const notes = annualWellnessNotes || [];
  const totalNotes = notes.length;

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Annual Wellness 2.0 – Clinical Snapshot"
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
      />
      <Divider />
      <CardContent>
        {annualWellnessLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Loading Annual Wellness 2.0 notes from AdvancedMD…
          </Typography>
        )}

        {annualWellnessError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading Annual Wellness notes:{' '}
            {annualWellnessError.message || 'Unknown error'}
          </Alert>
        )}

        {!annualWellnessLoading &&
          totalNotes === 0 &&
          !annualWellnessError && (
            <Typography variant="body2" color="text.secondary">
              No Annual Wellness 2.0 notes available for this patient.
            </Typography>
          )}

        {!annualWellnessLoading &&
          !annualWellnessError &&
          totalNotes > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, textAlign: 'center' }}
            >
              {totalNotes === 1
                ? '1 Annual Wellness note'
                : `${totalNotes} Annual Wellness notes`}
            </Typography>
          )}

        {notes.map(note => {
          const f = note.fields || {};
          const dateLabel = note.noteDateTime
            ? new Date(note.noteDateTime).toLocaleString()
            : 'N/A';

          const bpCurrent =
            f.Vitals_BP_Systolic1 && f.Vitals_BP_Diastolic1
              ? `${f.Vitals_BP_Systolic1}/${f.Vitals_BP_Diastolic1}`
              : '';

          const bpPrev =
            f.Vitals_BP_SystolicPrev && f.Vitals_BP_DiastolicPrev
              ? `${f.Vitals_BP_SystolicPrev}/${f.Vitals_BP_DiastolicPrev}`
              : '';

          // 🔹 mini resumen para el encabezado
          const vitalsSummary = [
            f.Vitals_Weight ? `Wt: ${f.Vitals_Weight}` : null,
            f.Vitals_BMI ? `BMI: ${f.Vitals_BMI}` : null,
            bpCurrent ? `BP: ${bpCurrent}` : null,
          ]
            .filter(Boolean)
            .join(' • ');

          const isOpen = !!openMap[note.noteId];

          return (
            <Box
              key={note.noteId}
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* Header compacto de la nota */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {note.templateName} – {dateLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Entered by: {note.userName} | Signed by: {note.signedByUser}
                {note.reviewedByUser
                  ? ` | Reviewed by: ${note.reviewedByUser}`
                  : ''}
              </Typography>

              {vitalsSummary && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {vitalsSummary}
                </Typography>
              )}

              {/* Botón ver/ocultar detalle */}
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => toggleNote(note.noteId)}
                  startIcon={<EventNoteIcon fontSize="small" />}
                >
                  {isOpen ? 'Hide details' : 'View details'}
                </Button>
              </Box>

              {/* Detalle completo dentro de Collapse */}
              <Collapse in={isOpen}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Vitals */}
                  <Grid item xs={12} md={4}>
                    <Section
                      icon={<MonitorHeartIcon color="primary" />}
                      title="Vitals"
                    >
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Weight</TableCell>
                            <TableCell>
                              {f.Vitals_Weight || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Height</TableCell>
                            <TableCell>
                              {f.Vitals_Height || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>BMI</TableCell>
                            <TableCell>
                              {f.Vitals_BMI || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Temp</TableCell>
                            <TableCell>
                              {f.Vitals_Temperature || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Pulse</TableCell>
                            <TableCell>
                              {f.Vitals_Pulse || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>O₂ Sat RA</TableCell>
                            <TableCell>
                              {f.Vitals_O2SatRA || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>BP (current)</TableCell>
                            <TableCell>{bpCurrent || '—'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>BP (previous)</TableCell>
                            <TableCell>{bpPrev || '—'}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Section>
                  </Grid>

                  {/* HPI + meds + Preventive medicine */}
                  <Grid item xs={12} md={4}>
                    <Section
                      icon={<EventNoteIcon color="secondary" />}
                      title="HPI & Medications"
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                      >
                        History of Present Illness
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {f.HistoryofPresentIllness || '—'}
                      </Typography>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Current Medications
                        </Typography>
                        <Typography variant="body2">
                          {f.CurrentMedication || '—'}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Preventive Medicine
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {f.PreventiveMedicine || '—'}
                        </Typography>
                      </Box>
                    </Section>
                  </Grid>

                  {/* Residence + ADL + Geriatric wellness */}
                  <Grid item xs={12} md={4}>
                    <Section
                      icon={<HomeIcon color="info" />}
                      title="Residence & Function"
                    >
                      {/* Residence */}
                      <Typography variant="subtitle2" gutterBottom>
                        Residence
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>
                              {f.Residence_Type || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Dwelling</TableCell>
                            <TableCell>
                              {f.Residence_DwellingTypePrivate || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Lives With</TableCell>
                            <TableCell>
                              {f.Residence_LivesWithPrivate || '—'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Helps</TableCell>
                            <TableCell>
                              {f.Residence_WhoHelpPrivate || '—'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>

                      {/* Falls / Exercise / Devices */}
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <ElderlyIcon
                            fontSize="small"
                            color="action"
                            sx={{ mr: 1 }}
                          />
                          Geriatric Wellness
                        </Typography>
                        <Typography variant="body2">
                          Falls last year:{' '}
                          {f.GeriatricWellness_FallsInLastYear || '—'}
                        </Typography>
                        <Typography variant="body2">
                          Exercise:{' '}
                          {f.GeriatricWellness_ExerciseYesOrNo || '—'}
                        </Typography>
                        <Typography variant="body2">
                          Assistive devices:{' '}
                          {f.GeriatricWellness_AssistiveDevices || '—'}
                        </Typography>
                      </Box>

                      {/* ADL (versión resumida) */}
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <ChecklistIcon
                            fontSize="small"
                            color="action"
                            sx={{ mr: 1 }}
                          />
                          ADL – Key Items
                        </Typography>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>Eating</TableCell>
                              <TableCell>
                                {f.ADL_Eating || '—'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Dressing</TableCell>
                              <TableCell>
                                {f.ADL_Dressing || '—'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Bathing/Grooming</TableCell>
                              <TableCell>
                                {f.ADL_BathingGrooming || '—'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Ambulating</TableCell>
                              <TableCell>
                                {f.ADL_Ambulating || '—'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Managing Medications</TableCell>
                              <TableCell>
                                {f.ADL_ManagingMedications || '—'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Managing Finances</TableCell>
                              <TableCell>
                                {f.ADL_ManagingFinances || '—'}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    </Section>
                  </Grid>
                </Grid>
              </Collapse>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default AnnualWellnessSummary;
